/** Project budget versus actuals and recorded costs. */
import { Suspense, lazy, useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import {
  CheckboxField,
  DateInput,
  SelectInput,
  toDateValue,
} from "../../components/ui/controls.js";
import { apiDelete, apiGet, apiPost, apiPut } from "../../lib/api.js";
import {
  Detail,
  DetailGrid,
  ErrorPanel,
  LoadingPanel,
  MutationError,
  Section,
  formatDate,
  formatMoney,
} from "../shared.js";
import type { ProjectFinancials } from "./types.js";

export const ProjectBudgetChart = lazy(() =>
  import("@nivo/bar").then((module) => ({ default: module.ResponsiveBar })),
);

export const BUDGET_FIELDS = [
  "revenueBudget",
  "laborBudget",
  "materialBudget",
  "expenseBudget",
  "contingency",
] as const;

export type BudgetDraft = Record<(typeof BUDGET_FIELDS)[number], string>;

export const COST_KINDS = ["material", "subcontractor", "other"] as const;

export function ProjectFinances({
  id,
  canManage,
}: {
  id: string;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [includeChildren, setIncludeChildren] = useState(false);
  const [budgetDraft, setBudgetDraft] = useState<BudgetDraft | null>(null);
  const [costDraft, setCostDraft] = useState({
    kind: "other" as (typeof COST_KINDS)[number],
    date: toDateValue(new Date()),
    description: "",
    amount: "",
  });
  const query = useQuery({
    queryKey: ["project-financials", id, includeChildren],
    queryFn: () =>
      apiGet<{ data: ProjectFinancials }>(`/projects/${id}/financials`, {
        query: { includeChildren },
      }).then((response) => response.data),
  });
  const invalidate = () =>
    client.invalidateQueries({ queryKey: ["project-financials", id] });
  const saveBudget = useMutation({
    mutationFn: (draft: BudgetDraft) =>
      apiPut(`/projects/${id}/budget`, {
        version: query.data?.budget?.version ?? 1,
        ...draft,
      }),
    onSuccess: async () => {
      setBudgetDraft(null);
      await invalidate();
    },
  });
  const createCost = useMutation({
    mutationFn: () => apiPost(`/projects/${id}/costs`, costDraft),
    onSuccess: async () => {
      setCostDraft((current) => ({ ...current, description: "", amount: "" }));
      await invalidate();
    },
  });
  const removeCost = useMutation({
    mutationFn: (cost: { id: string; version: number }) =>
      apiDelete(`/projects/${id}/costs/${cost.id}?version=${cost.version}`),
    onSuccess: invalidate,
  });
  if (query.isLoading) return <LoadingPanel />;
  if (query.error || !query.data) return <ErrorPanel error={query.error} />;
  const data = query.data;
  const money = (value: string) => formatMoney(value, data.currency);
  const budget: BudgetDraft = budgetDraft ?? {
    revenueBudget: data.budget?.revenueBudget ?? "0.00",
    laborBudget: data.budget?.laborBudget ?? "0.00",
    materialBudget: data.budget?.materialBudget ?? "0.00",
    expenseBudget: data.budget?.expenseBudget ?? "0.00",
    contingency: data.budget?.contingency ?? "0.00",
  };
  const chartData = [
    {
      category: t("projects.laborBudget"),
      budget: Number(data.budget?.laborBudget ?? 0),
      actual: Number(data.actual.laborCost),
    },
    {
      category: t("projects.materialBudget"),
      budget: Number(data.budget?.materialBudget ?? 0),
      actual: Number(data.actual.materialCost),
    },
    {
      category: t("projects.expenses"),
      budget: Number(data.budget?.expenseBudget ?? 0),
      actual: Number(data.actual.expenseCost),
    },
  ];
  const margin = Number(data.actual.margin);
  return (
    <div className="space-y-4">
      <CheckboxField
        id="financials-children"
        checked={includeChildren}
        onChange={setIncludeChildren}
        label={t("projects.includeChildren")}
      />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          [t("projects.acceptedOffers"), money(data.actual.acceptedOfferValue)],
          [t("projects.invoiced"), money(data.actual.invoiceRevenue)],
          [t("projects.totalCost"), money(data.actual.totalCost)],
          [t("projects.margin"), money(data.actual.margin)],
        ].map(([label, value], index) => (
          <div
            key={label}
            className="rounded-xl border border-app-border bg-app-surface p-4"
          >
            <p className="text-xs font-medium uppercase tracking-wide text-app-muted">
              {label}
            </p>
            <p
              className={`mt-1 text-xl font-semibold tabular-nums ${
                index === 3 ? (margin < 0 ? "text-danger" : "text-success") : ""
              }`}
            >
              {value}
            </p>
          </div>
        ))}
      </div>
      <Section title={t("projects.financialSummary")}>
        <DetailGrid>
          <Detail
            label={t("projects.paid")}
            value={money(data.actual.paymentRevenue)}
          />
          <Detail
            label={t("projects.credited")}
            value={money(data.actual.creditedAmount)}
          />
          <Detail
            label={t("projects.laborCost")}
            value={`${money(data.actual.laborCost)} · ${(data.actual.laborMinutes / 60).toFixed(1)}h`}
          />
          <Detail
            label={t("projects.materialBudget")}
            value={money(data.actual.materialCost)}
          />
          <Detail
            label={t("projects.expenses")}
            value={money(data.actual.expenseCost)}
          />
          <Detail
            label={t("projects.manualCosts")}
            value={money(data.actual.manualCost)}
          />
        </DetailGrid>
        {data.actual.missingLaborRates || data.actual.missingMaterialCosts ? (
          <p className="mt-3 text-sm text-warning">
            {t("projects.missingCostRates", {
              labor: data.actual.missingLaborRates,
              material: data.actual.missingMaterialCosts,
            })}
          </p>
        ) : null}
        {data.excludedCurrencies.length ? (
          <p className="mt-2 text-sm text-app-muted">
            {t("projects.excludedCurrencies")}{" "}
            {data.excludedCurrencies
              .map((row) => formatMoney(row.amount, row.currency))
              .join(", ")}
          </p>
        ) : null}
      </Section>
      <Section title={t("projects.budgetVsActual")}>
        <div className="h-64">
          <Suspense fallback={<LoadingPanel />}>
            <ProjectBudgetChart
              data={chartData}
              keys={["budget", "actual"]}
              indexBy="category"
              margin={{ top: 12, right: 16, bottom: 40, left: 64 }}
              padding={0.3}
              groupMode="grouped"
              enableLabel={false}
              role="img"
            />
          </Suspense>
        </div>
      </Section>
      {!includeChildren && canManage ? (
        <Section title={t("projects.budget")}>
          <form
            className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5"
            onSubmit={(event) => {
              event.preventDefault();
              saveBudget.mutate(budget);
            }}
          >
            {BUDGET_FIELDS.map((key) => (
              <Field key={key} label={t(`projects.${key}`)}>
                <Input
                  fluid
                  inputMode="decimal"
                  value={budget[key]}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setBudgetDraft({ ...budget, [key]: event.target.value })
                  }
                />
              </Field>
            ))}
            <div className="flex items-center gap-3 sm:col-span-2 lg:col-span-5">
              <Button
                type="submit"
                disabled={!budgetDraft}
                loading={saveBudget.isPending}
              >
                {t("common.save")}
              </Button>
              <MutationError error={saveBudget.error} />
            </div>
          </form>
        </Section>
      ) : null}
      <Section title={t("projects.manualCosts")}>
        {canManage && !includeChildren ? (
          <form
            className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[10rem_10rem_1fr_8rem_auto]"
            onSubmit={(event) => {
              event.preventDefault();
              createCost.mutate();
            }}
          >
            <SelectInput
              ariaLabel={t("common.type")}
              value={costDraft.kind}
              onChange={(kind) => setCostDraft({ ...costDraft, kind })}
              options={COST_KINDS.map((kind) => ({
                value: kind,
                label: t(`projects.costKind.${kind}`),
              }))}
            />
            <DateInput
              ariaLabel={t("common.date")}
              required
              value={costDraft.date}
              onChange={(date) => setCostDraft({ ...costDraft, date })}
            />
            <Input
              fluid
              required
              aria-label={t("common.description")}
              placeholder={t("common.description")}
              value={costDraft.description}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setCostDraft({ ...costDraft, description: event.target.value })
              }
            />
            <Input
              fluid
              required
              inputMode="decimal"
              aria-label={t("common.amount")}
              placeholder={t("common.amount")}
              value={costDraft.amount}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setCostDraft({
                  ...costDraft,
                  amount: event.target.value.replace(",", "."),
                })
              }
            />
            <Button
              type="submit"
              icon="pi pi-plus"
              loading={createCost.isPending}
            >
              {t("common.add")}
            </Button>
            <div className="sm:col-span-2 lg:col-span-5">
              <MutationError error={createCost.error || removeCost.error} />
            </div>
          </form>
        ) : null}
        {data.costs.length ? (
          <ul className="divide-y divide-app-border text-sm">
            {data.costs.map((cost) => (
              <li key={cost.id} className="flex items-center gap-3 py-2">
                <span className="w-24 text-app-muted">
                  {formatDate(cost.date)}
                </span>
                <span className="min-w-0 flex-1 truncate">
                  {cost.description}
                  <span className="ml-2 text-xs text-app-muted">
                    {t(`projects.costKind.${cost.kind}`)}
                  </span>
                </span>
                <span className="tabular-nums">{money(cost.amount)}</span>
                {canManage && !includeChildren ? (
                  <Button
                    type="button"
                    size="small"
                    variant="text"
                    severity="danger"
                    icon="pi pi-trash"
                    aria-label={t("common.delete")}
                    onClick={() => removeCost.mutate(cost)}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-app-muted">{t("projects.noCosts")}</p>
        )}
      </Section>
    </div>
  );
}
